import { Component, EventEmitter, Input, Output, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CancelInvoicePayload, InvoiceHeaderResponse } from '../../../../../../core/models/invoice.model';
import { ActionBackOfficeResponse } from '../../../../../../core/models/api.model';
import { ApiService } from '../../../../../../core/services/api.service';
import { VatSettingService } from '../../../../../../core/services/vat-setting.service';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { PssLoadingComponent } from '../../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalInvoicePdfViewerComponent } from '../modal-invoice-pdf-viewer/modal-invoice-pdf-viewer.component';
import { ModalAdminActivityHistoryComponent } from '../../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-invoice-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalInvoicePdfViewerComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-invoice-detail.component.html',
  styleUrls: ['./modal-invoice-detail.component.css']
})
export class ModalInvoiceDetailComponent implements OnInit {
  @Input() id: number = 0;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  invoice: InvoiceHeaderResponse = {} as InvoiceHeaderResponse;
  isLoading = false;
  isProcessing = false;
  today: Date = new Date();
  userId: number = 0;

  showCancelModal = false;
  cancelReason = '';
  showCancelPrintStep = false;

  isProcessingPdf = false;
  showPdfModal = false;
  pdfSrc: string | null = null;
  pdfModalTitle = 'ใบแจ้งหนี้ (PDF)';
  pdfDownloadName = 'invoice.pdf';

  readonly vatSetting = inject(VatSettingService);

  constructor(
    private apiService: ApiService,
    private toast: ToastService,
    private cookieService: CookieService
  ) { }

  get calculatedSubtotal(): number {
    if (!this.invoice || !this.invoice.invoiceDetailResponses) return 0;
    return this.invoice.invoiceDetailResponses.reduce((sum, item) => sum + ((item.fee || 0) * (item.quantity || 1)), 0);
  }

  get calculatedVat(): number {
    return this.invoice.vat;
  }

  get calculatedNetTotal(): number {
    return this.calculatedSubtotal + this.calculatedVat;
  }

  get canConfirmCancel(): boolean {
    return this.cancelReason.trim().length >= 3 && !this.isProcessing;
  }

  getStatusClass(status: number): string {
    switch (status) {
      case 0: return 'bg-gray-100 text-gray-600 border-gray-200';
      case 1: return 'bg-amber-50 text-amber-600 border-amber-200';
      case 2: return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      case 3: return 'bg-rose-50 text-rose-600 border-rose-200';
      default: return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  }

  getStatusLabel(status: number): string {
    switch (status) {
      case 0: return 'ร่างเอกสาร';
      case 1: return 'รอชำระเงิน';
      case 2: return 'ชำระเงินแล้ว';
      case 3: return 'ยกเลิก';
      default: return 'ไม่ระบุ';
    }
  }

  ngOnInit() {
    this.initializeUserId();
    if (this.id) {
      this.getData();
    }
  }

  private initializeUserId() {
    const userIdFromSession = sessionStorage.getItem('user_id');
    const userIdFromCookie = this.cookieService.get('user_id');
    this.userId = userIdFromCookie ? parseInt(userIdFromCookie) : (userIdFromSession ? JSON.parse(userIdFromSession) : 0);
  }

  onCancelVerify() {
    this.cancelReason = '';
    this.showCancelPrintStep = false;
    this.showCancelModal = true;
  }

  closeCancelModal() {
    this.showCancelModal = false;
    this.cancelReason = '';
    this.showCancelPrintStep = false;
  }

  onCancelBackdropClick() {
    if (!this.showCancelPrintStep) {
      this.closeCancelModal();
    }
  }

  finishCancelFlow() {
    this.closeCancelModal();
    this.save.emit();
  }

  onClose() {
    this.close.emit();
  }

  getData() {
    this.isLoading = true;
    this.invoice = {} as InvoiceHeaderResponse;

    this.apiService.getCityParkingInvoiceById(this.id).subscribe({
      next: (res) => {
        this.invoice = res;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching invoice:', err);
        this.isLoading = false;
      }
    });
  }

  confirmCancel() {
    const reason = this.cancelReason.trim();
    if (reason.length < 3) {
      this.toast.error('กรุณาระบุสาเหตุ / หมายเหตุการยกเลิก');
      return;
    }

    this.isProcessing = true;

    const payload: CancelInvoicePayload = {
      invoiceId: this.id,
      userId: this.userId,
      reason,
    };

    this.apiService.cancelInvoice(payload).subscribe({
      next: (res: ActionBackOfficeResponse) => {
        this.isProcessing = false;
        if (res.isSuccess) {
          this.toast.success(res.message || 'ยกเลิกใบแจ้งหนี้เรียบร้อย');
          this.invoice.status = 3;
          this.showCancelPrintStep = true;
        } else {
          this.toast.error(res.message || 'เกิดข้อผิดพลาดในการยกเลิก');
        }
      },
      error: (err) => {
        this.isProcessing = false;
        console.error('Error cancelling invoice:', err);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ');
      }
    });
  }

  print() {
    this.openInvoicePdf();
  }

  openInvoicePdf() {
    this.isProcessingPdf = true;
    this.pdfModalTitle = `ใบแจ้งหนี้ ${this.invoice.invoiceNo || ''}`;
    this.pdfDownloadName = `invoice-${this.invoice.invoiceNo || this.id}.pdf`;
    this.apiService.getInvoicePdf(this.id).subscribe({
      next: (blob) => {
        this.pdfSrc = URL.createObjectURL(blob);
        this.showPdfModal = true;
        this.isProcessingPdf = false;
      },
      error: (err) => {
        console.error('Error fetching invoice PDF:', err);
        this.toast.error('เกิดข้อผิดพลาดในการดึงไฟล์ PDF ใบแจ้งหนี้');
        this.isProcessingPdf = false;
      }
    });
  }

  openCancelRequestPdf() {
    this.isProcessingPdf = true;
    this.pdfModalTitle = `เอกสารขอยกเลิก ${this.invoice.invoiceNo || ''}`;
    this.pdfDownloadName = `invoice-cancel-request-${this.invoice.invoiceNo || this.id}.pdf`;
    this.apiService.getInvoiceCancelRequestPdf(this.id).subscribe({
      next: (blob) => {
        this.pdfSrc = URL.createObjectURL(blob);
        this.showPdfModal = true;
        this.isProcessingPdf = false;
      },
      error: (err) => {
        console.error('Error fetching cancel-request PDF:', err);
        this.toast.error('เกิดข้อผิดพลาดในการดึงไฟล์ PDF เอกสารขอยกเลิก');
        this.isProcessingPdf = false;
      }
    });
  }

  formatDateDisplay(date: Date | string | null | undefined): string {
    if (!date) return '-';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  formatDateTimeDisplay(date: Date | string | null | undefined): string {
    if (!date) return '-';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hh}:${mm}`;
  }

  get canShowActivityHistory(): boolean {
    return this.id > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
